import {
  TableOptions,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';
import { TableSettings } from './types';

export function useTableStateManagement<T>(settings: TableSettings) {
  const [sorting, setSorting] = settings.sorting || [];
  const [grouping, setGrouping] = settings.grouping || [];
  const [filtering, setFiltering] = settings.filtering || [];
  const [searching, setSearching] = settings.searching || [];
  const [ordering, setColumnOrdering] = settings.ordering || [];
  const [selected, setSelected] = settings.selected || [];
  const [sizing, setSizing] = settings.sizing || [];
  const [columnVisibility, setColumnVisibility] = settings.visibility || [];
  const [expanded, setExpanded] = settings.expanding || [true, undefined];

  const tableGetters = {
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  };

  const tableStateManagement: Omit<TableOptions<T>, 'data' | 'columns'> = {
    state: {
      sorting,
      grouping,
      columnOrder: ordering,
      columnFilters: filtering,
      globalFilter: searching,
      columnSizing: sizing,
      columnPinning: { left: ['select'], right: [] },
      columnVisibility,
      expanded,
      rowSelection: selected,
    },

    ...(setSorting && { onSortingChange: setSorting }),
    ...(setGrouping && { onGroupingChange: setGrouping }),
    ...(setFiltering && { onColumnFiltersChange: setFiltering }),
    ...(setSearching && { onGlobalFilterChange: setSearching }),
    ...(setColumnOrdering && { onColumnOrderChange: setColumnOrdering }),
    ...(setSizing && { onColumnSizingChange: setSizing }),
    ...(setColumnVisibility && {
      onColumnVisibilityChange: setColumnVisibility,
    }),
    ...(setExpanded && { onExpandedChange: setExpanded }),
    ...(setSelected && { onRowSelectionChange: setSelected }),

    columnResizeMode: 'onChange',

    ...tableGetters,
    autoResetExpanded: false,
    enableMultiRowSelection: true,
    enableMultiSort: true,
    enableHiding: true,
    groupedColumnMode: false,
  };

  return tableStateManagement;
}
